import { useMutation, useQuery } from '@tanstack/react-query';
import type {
    DataTag,
    DefinedInitialDataOptions,
    DefinedUseQueryResult,
    MutationFunction,
    QueryClient,
    QueryFunction,
    QueryKey,
    UndefinedInitialDataOptions,
    UseMutationOptions,
    UseMutationResult,
    UseQueryOptions,
    UseQueryResult
} from '@tanstack/react-query';

import { useCallback } from 'react';

import type {
    CreateFormingSessionModel,
    GetFormingSessionByIdResponse,
    ProblemDetails,
    SimulateArticlePlacementParams,
    SimulateArticlePlacementResponse
} from '../../models';

import { useCustomAppClient } from '../../custom-client';
import type { ErrorType } from '../../custom-client';

/**
 * Get a plan by its id
 */
export const useGetFormingSessionByIdHook = () => {
    const getFormingSessionById = useCustomAppClient<GetFormingSessionByIdResponse>();

    return useCallback(
        (id: string, signal?: AbortSignal) => {
            return getFormingSessionById({ url: `/formingsessions/${id}`, method: 'GET', signal });
        },
        [getFormingSessionById]
    );
};

export const getGetFormingSessionByIdQueryKey = (id?: string) => {
    return [`/formingsessions/${id}`] as const;
};

export const useGetFormingSessionByIdQueryOptions = <
    TData = Awaited<ReturnType<ReturnType<typeof useGetFormingSessionByIdHook>>>,
    TError = ErrorType<ProblemDetails>
>(
    id: string,
    options?: {
        query?: Partial<
            UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetFormingSessionByIdHook>>>, TError, TData>
        >;
    }
) => {
    const { query: queryOptions } = options ?? {};

    const queryKey = queryOptions?.queryKey ?? getGetFormingSessionByIdQueryKey(id);

    const getFormingSessionById = useGetFormingSessionByIdHook();

    const queryFn: QueryFunction<Awaited<ReturnType<ReturnType<typeof useGetFormingSessionByIdHook>>>> = ({ signal }) =>
        getFormingSessionById(id, signal);

    return { queryKey, queryFn, enabled: !!id, ...queryOptions } as UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useGetFormingSessionByIdHook>>>,
        TError,
        TData
    > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type GetFormingSessionByIdQueryResult = NonNullable<
    Awaited<ReturnType<ReturnType<typeof useGetFormingSessionByIdHook>>>
>;
export type GetFormingSessionByIdQueryError = ErrorType<ProblemDetails>;

export function useGetFormingSessionById<
    TData = Awaited<ReturnType<ReturnType<typeof useGetFormingSessionByIdHook>>>,
    TError = ErrorType<ProblemDetails>
>(
    id: string,
    options: {
        query: Partial<
            UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetFormingSessionByIdHook>>>, TError, TData>
        > &
            Pick<
                DefinedInitialDataOptions<
                    Awaited<ReturnType<ReturnType<typeof useGetFormingSessionByIdHook>>>,
                    TError,
                    Awaited<ReturnType<ReturnType<typeof useGetFormingSessionByIdHook>>>
                >,
                'initialData'
            >;
    },
    queryClient?: QueryClient
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetFormingSessionById<
    TData = Awaited<ReturnType<ReturnType<typeof useGetFormingSessionByIdHook>>>,
    TError = ErrorType<ProblemDetails>
>(
    id: string,
    options?: {
        query?: Partial<
            UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetFormingSessionByIdHook>>>, TError, TData>
        > &
            Pick<
                UndefinedInitialDataOptions<
                    Awaited<ReturnType<ReturnType<typeof useGetFormingSessionByIdHook>>>,
                    TError,
                    Awaited<ReturnType<ReturnType<typeof useGetFormingSessionByIdHook>>>
                >,
                'initialData'
            >;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetFormingSessionById<
    TData = Awaited<ReturnType<ReturnType<typeof useGetFormingSessionByIdHook>>>,
    TError = ErrorType<ProblemDetails>
>(
    id: string,
    options?: {
        query?: Partial<
            UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetFormingSessionByIdHook>>>, TError, TData>
        >;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

export function useGetFormingSessionById<
    TData = Awaited<ReturnType<ReturnType<typeof useGetFormingSessionByIdHook>>>,
    TError = ErrorType<ProblemDetails>
>(
    id: string,
    options?: {
        query?: Partial<
            UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetFormingSessionByIdHook>>>, TError, TData>
        >;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
    const queryOptions = useGetFormingSessionByIdQueryOptions(id, options);

    const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
        queryKey: DataTag<QueryKey, TData, TError>;
    };

    query.queryKey = queryOptions.queryKey;

    return query;
}

/**
 * Create a new formingsession
 */
export const useCreateFormingSessionHook = () => {
    const createFormingSession = useCustomAppClient<GetFormingSessionByIdResponse>();

    return useCallback(
        (createFormingSessionModel: CreateFormingSessionModel, signal?: AbortSignal) => {
            return createFormingSession({
                url: `/formingsessions`,
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                data: createFormingSessionModel,
                signal
            });
        },
        [createFormingSession]
    );
};

export const useCreateFormingSessionMutationOptions = <
    TError = ErrorType<ProblemDetails>,
    TContext = unknown
>(options?: {
    mutation?: UseMutationOptions<
        Awaited<ReturnType<ReturnType<typeof useCreateFormingSessionHook>>>,
        TError,
        { data: CreateFormingSessionModel },
        TContext
    >;
}): UseMutationOptions<
    Awaited<ReturnType<ReturnType<typeof useCreateFormingSessionHook>>>,
    TError,
    { data: CreateFormingSessionModel },
    TContext
> => {
    const mutationKey = ['createFormingSession'];
    const { mutation: mutationOptions } = options
        ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
            ? options
            : { ...options, mutation: { ...options.mutation, mutationKey } }
        : { mutation: { mutationKey } };

    const createFormingSession = useCreateFormingSessionHook();

    const mutationFn: MutationFunction<
        Awaited<ReturnType<ReturnType<typeof useCreateFormingSessionHook>>>,
        { data: CreateFormingSessionModel }
    > = (props) => {
        const { data } = props ?? {};

        return createFormingSession(data);
    };

    return { mutationFn, ...mutationOptions };
};

export type CreateFormingSessionMutationResult = NonNullable<
    Awaited<ReturnType<ReturnType<typeof useCreateFormingSessionHook>>>
>;
export type CreateFormingSessionMutationBody = CreateFormingSessionModel;
export type CreateFormingSessionMutationError = ErrorType<ProblemDetails>;

export const useCreateFormingSession = <TError = ErrorType<ProblemDetails>, TContext = unknown>(
    options?: {
        mutation?: UseMutationOptions<
            Awaited<ReturnType<ReturnType<typeof useCreateFormingSessionHook>>>,
            TError,
            { data: CreateFormingSessionModel },
            TContext
        >;
    },
    queryClient?: QueryClient
): UseMutationResult<
    Awaited<ReturnType<ReturnType<typeof useCreateFormingSessionHook>>>,
    TError,
    { data: CreateFormingSessionModel },
    TContext
> => {
    const mutationOptions = useCreateFormingSessionMutationOptions(options);

    return useMutation(mutationOptions, queryClient);
};
/**
 * Simulate placing multiple articles in a forming session for testing and demonstration purposes
 */
export const useSimulateArticlePlacementHook = () => {
    const simulateArticlePlacement = useCustomAppClient<SimulateArticlePlacementResponse>();

    return useCallback(
        (simulateArticlePlacementBody: string[], params?: SimulateArticlePlacementParams, signal?: AbortSignal) => {
            return simulateArticlePlacement({
                url: `/formingsessions/simulate-articles`,
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                data: simulateArticlePlacementBody,
                params,
                signal
            });
        },
        [simulateArticlePlacement]
    );
};

export const useSimulateArticlePlacementMutationOptions = <
    TError = ErrorType<ProblemDetails>,
    TContext = unknown
>(options?: {
    mutation?: UseMutationOptions<
        Awaited<ReturnType<ReturnType<typeof useSimulateArticlePlacementHook>>>,
        TError,
        { data: string[]; params?: SimulateArticlePlacementParams },
        TContext
    >;
}): UseMutationOptions<
    Awaited<ReturnType<ReturnType<typeof useSimulateArticlePlacementHook>>>,
    TError,
    { data: string[]; params?: SimulateArticlePlacementParams },
    TContext
> => {
    const mutationKey = ['simulateArticlePlacement'];
    const { mutation: mutationOptions } = options
        ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
            ? options
            : { ...options, mutation: { ...options.mutation, mutationKey } }
        : { mutation: { mutationKey } };

    const simulateArticlePlacement = useSimulateArticlePlacementHook();

    const mutationFn: MutationFunction<
        Awaited<ReturnType<ReturnType<typeof useSimulateArticlePlacementHook>>>,
        { data: string[]; params?: SimulateArticlePlacementParams }
    > = (props) => {
        const { data, params } = props ?? {};

        return simulateArticlePlacement(data, params);
    };

    return { mutationFn, ...mutationOptions };
};

export type SimulateArticlePlacementMutationResult = NonNullable<
    Awaited<ReturnType<ReturnType<typeof useSimulateArticlePlacementHook>>>
>;
export type SimulateArticlePlacementMutationBody = string[];
export type SimulateArticlePlacementMutationError = ErrorType<ProblemDetails>;

export const useSimulateArticlePlacement = <TError = ErrorType<ProblemDetails>, TContext = unknown>(
    options?: {
        mutation?: UseMutationOptions<
            Awaited<ReturnType<ReturnType<typeof useSimulateArticlePlacementHook>>>,
            TError,
            { data: string[]; params?: SimulateArticlePlacementParams },
            TContext
        >;
    },
    queryClient?: QueryClient
): UseMutationResult<
    Awaited<ReturnType<ReturnType<typeof useSimulateArticlePlacementHook>>>,
    TError,
    { data: string[]; params?: SimulateArticlePlacementParams },
    TContext
> => {
    const mutationOptions = useSimulateArticlePlacementMutationOptions(options);

    return useMutation(mutationOptions, queryClient);
};
